<template>
  <v-container fluid grid-list-md pl-0 pt-2 class="grey lighten-2">
    <v-layout wrap>
      <v-flex xs12>
        <v-toolbar class="pa-2">
          <v-avatar class="secondary">
            <v-icon dark>bar_chart</v-icon>
          </v-avatar>
          <v-toolbar-title>
            <div class="ml-3">
              <h4 class="headline">
                Referral Stats
                <span v-if="DashboardPartnerHasSubAccounts">(Sub-account Stats)</span>
              </h4>
            </div>
          </v-toolbar-title>

          <v-spacer></v-spacer>

          <v-col cols="2">
            <v-menu
              ref="menu"
              :close-on-content-click="false"
              v-model="showStartDatePicker"
              transition="scale-transition"
              offset-y
              full-width
              :nudge-right="40"
              min-width="290px"
              :return-value.sync="startDate"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  label="Start date"
                  v-model="startDateUK"
                  prepend-icon="event"
                  hide-details
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                @input="$refs.menu.save(startDate)"
                min="2016-06-20"
                :max="endDate"
                first-day-of-week="1"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="2">
            <v-menu
              ref="menu2"
              :close-on-content-click="false"
              v-model="showEndDatePicker"
              transition="scale-transition"
              offset-y
              full-width
              :nudge-right="40"
              min-width="290px"
              :return-value.sync="endDate"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  label="End date"
                  v-model="endDateUK"
                  prepend-icon="event"
                  hide-details
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                @input="$refs.menu2.save(endDate)"
                :min="startDate"
                :max="today"
                first-day-of-week="1"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-toolbar-items>
            <v-btn text @click="getData" :loading="DashboardDataLoading">Apply</v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Total Referrals"
          :value="getSplitStats('referrals')"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Total Registrations"
          :value="getSplitStats('registrations')"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Total Enquiries"
          :value="getSplitStats('enquiriesSent')"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Accepted Enquiries"
          :value="getSplitStats('enquiriesAccepted')"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Funded Enquiries"
          :value="getSplitStats('SMEsFunded')"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Avg. Amount Requested"
          :value="getSplitStats('averageAmountRequested', true)"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Avg. Funds Released"
          :value="getSplitStats('averageAmountFunded', true)"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
      <v-flex xs6 sm3>
        <EnquiryCard
          title="Total Funds Released"
          :value="getSplitStats('totalFundsReleased', true)"
          colour="indigo darken-4"
          dark
        ></EnquiryCard>
      </v-flex>
    </v-layout>

    <v-layout wrap>
      <v-flex xs12 sm6 xl3>
        <v-card height="100%">
          <v-card-title primary-title>
            <div>
              <h3 class="headline mb-0">Referrals by Company Type</h3>
            </div>
          </v-card-title>
          <div class="chart-card">
            <DoughnutChart :chartData="DashboardPartnerEnquiriesByProductType" />
          </div>
        </v-card>
      </v-flex>

      <v-flex xs12 sm6 xl3>
        <v-card height="100%">
          <v-card-title primary-title>
            <div>
              <h3 class="headline mb-0">Referrals by Credit Profile</h3>
            </div>
          </v-card-title>
          <div class="chart-card">
            <DoughnutChart :chartData="DashboardPartnerReferralsByCreditProfile" />
          </div>
        </v-card>
      </v-flex>

      <v-flex md12 xl6>
        <v-card>
          <v-card-title primary-title>
            <div>
              <h3 class="headline mb-0">Monthly Referrals</h3>
              <div>For all referrals added to date</div>
            </div>
          </v-card-title>
          <div class="pa-3">
            <BarChart :chartData="DashboardPartnerMonthlyFundedReferrals" />
          </div>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import BarChart from './BarChart';
import DoughnutChart from './DoughnutChart';
import EnquiryCard from './EnquiryCard';

const DATE_FORMAT = 'YYYY-MM-DD';

export default {
  name: 'PartnerStatsView',
  components: { BarChart, DoughnutChart, EnquiryCard },
  props: {
    partnerId: String
  },
  data: () => ({
    startDate: moment()
      .subtract(30, 'days')
      .format(DATE_FORMAT),
    endDate: moment().format(DATE_FORMAT),
    today: moment().format(DATE_FORMAT),
    showStartDatePicker: false,
    showEndDatePicker: false
  }),
  computed: {
    ...mapGetters([
      'DashboardDataLoading',
      'DashboardPartnerStats',
      'DashboardPartnerEnquiriesByProductType',
      'DashboardPartnerReferralsByCreditProfile',
      'DashboardPartnerMonthlyFundedReferrals',
      'DashboardPartnerHasSubAccounts',
      'DashboardPartnerSubAccountStats'
    ]),
    startDateUK: function() {
      return moment(this.startDate).format('DD/MM/YYYY');
    },
    endDateUK: function() {
      return moment(this.endDate).format('DD/MM/YYYY');
    }
  },
  methods: {
    getData: function() {
      this.$store.dispatch('getPartnerData', {
        startDate: this.startDate,
        endDate: this.endDate,
        partnerId: this.partnerId
      });
    },
    getSplitStats(statName, formatAsCurrency = false) {
      let subAccountStat = null;

      const mainStat = formatAsCurrency
        ? this.$options.filters.currency(this.DashboardPartnerStats[statName])
        : this.DashboardPartnerStats[statName];

      if (this.DashboardPartnerHasSubAccounts) {
        subAccountStat = formatAsCurrency
          ? this.$options.filters.currency(this.DashboardPartnerSubAccountStats[statName])
          : this.DashboardPartnerSubAccountStats[statName];
      }

      return this.DashboardPartnerHasSubAccounts ? `${mainStat} (${subAccountStat})` : mainStat;
    }
  },
  watch: {
    partnerId() {
      this.getData();
    }
  },
  created() {
    this.getData();
  }
};
</script>

<style type="scss" scoped>
.chart-card {
  padding: 2em;
}

.v-card__title .v-text-field {
  padding-top: 0;
}
</style>
